import { useState, useEffect, useRef, useCallback } from "react";
import { Timer, Play, Pause, Stop, X } from "@phosphor-icons/react";

const PRESETS = [
  { label: "30s", seconds: 30 },
  { label: "60s", seconds: 60 },
  { label: "90s", seconds: 90 },
  { label: "2m", seconds: 120 },
  { label: "3m", seconds: 180 },
];

export default function RestTimer() {
  const [open, setOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [running, setRunning] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const intervalRef = useRef(null);
  const audioRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (running && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearTimer();
            setRunning(false);
            // Notify the rest of the app (session screen, etc.) that rest just ended
            try {
              window.dispatchEvent(new CustomEvent("uptop:rest-done"));
            } catch (e) { /* silent */ }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return clearTimer;
  }, [running, timeLeft, clearTimer]);

  const startTimer = (seconds) => {
    clearTimer();
    setTimeLeft(seconds);
    setTotalTime(seconds);
    setRunning(true);
    setOpen(true);
  };

  const togglePause = () => {
    if (running) {
      clearTimer();
      setRunning(false);
    } else if (timeLeft > 0) {
      setRunning(true);
    }
  };

  const stopTimer = () => {
    clearTimer();
    setRunning(false);
    setTimeLeft(0);
    setTotalTime(0);
  };

  const handleCustomStart = () => {
    const secs = parseInt(customInput);
    if (secs > 0 && secs <= 600) {
      startTimer(secs);
      setCustomInput("");
    }
  };

  // External trigger via custom DOM event (used by WorkoutSession auto-rest)
  useEffect(() => {
    const handler = (e) => {
      const secs = parseInt(e?.detail?.seconds);
      if (secs > 0 && secs <= 600) {
        startTimer(secs);
      }
    };
    window.addEventListener("uptop:start-rest", handler);
    return () => window.removeEventListener("uptop:start-rest", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const progress = totalTime > 0 ? ((totalTime - timeLeft) / totalTime) * 100 : 0;
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;

  // Minimized floating indicator when timer is running but panel is closed
  if (!open && (running || timeLeft > 0)) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-6 z-50 bg-[#E05A1D] text-white w-14 h-14 rounded-full flex items-center justify-center shadow-lg shadow-[#E05A1D]/20 transition-all hover:scale-105"
        data-testid="rest-timer-fab"
      >
        <span className="text-sm font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {formatTime(timeLeft)}
        </span>
      </button>
    );
  }

  // Collapsed FAB
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-6 z-50 bg-[#161616] border border-[#27272A] text-[#A1A1AA] hover:text-[#E05A1D] hover:border-[#E05A1D] w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105"
        data-testid="rest-timer-fab"
      >
        <Timer size={22} weight="bold" />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-20 right-6 z-50 bg-[#161616] border border-[#27272A] shadow-xl shadow-black/40 w-72"
      data-testid="rest-timer-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272A]">
        <div className="flex items-center gap-2">
          <Timer size={16} className="text-[#E05A1D]" weight="bold" />
          <span
            className="text-sm font-bold uppercase tracking-wider text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Rest Timer
          </span>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-[#71717A] hover:text-white transition-colors"
          data-testid="rest-timer-close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="p-4">
        {/* Circular Timer Display */}
        <div className="flex justify-center mb-4">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r={radius} fill="none" stroke="#27272A" strokeWidth="4" />
              <circle
                cx="60" cy="60" r={radius} fill="none"
                stroke={timeLeft === 0 && totalTime > 0 ? "#22C55E" : "#E05A1D"}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                className="transition-all duration-1000 ease-linear"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span
                className={`text-3xl font-black ${timeLeft === 0 && totalTime > 0 ? 'text-[#22C55E]' : 'text-white'}`}
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                data-testid="timer-display"
              >
                {formatTime(timeLeft)}
              </span>
              {timeLeft === 0 && totalTime > 0 && (
                <span className="text-xs text-[#22C55E] font-bold uppercase">Done!</span>
              )}
            </div>
          </div>
        </div>

        {/* Controls */}
        {(running || timeLeft > 0) && (
          <div className="flex items-center justify-center gap-3 mb-4">
            <button
              onClick={togglePause}
              className="bg-[#27272A] hover:bg-[#3f3f46] text-white w-10 h-10 flex items-center justify-center transition-colors"
              data-testid="timer-pause-btn"
            >
              {running ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
            </button>
            <button
              onClick={stopTimer}
              className="bg-[#27272A] hover:bg-[#3f3f46] text-white w-10 h-10 flex items-center justify-center transition-colors"
              data-testid="timer-stop-btn"
            >
              <Stop size={18} weight="fill" />
            </button>
          </div>
        )}

        {/* Presets */}
        <div className="grid grid-cols-5 gap-1.5 mb-3">
          {PRESETS.map(p => (
            <button
              key={p.seconds}
              onClick={() => startTimer(p.seconds)}
              className={`text-xs font-bold uppercase py-2 text-center transition-colors border ${
                totalTime === p.seconds && (running || timeLeft > 0)
                  ? "bg-[#E05A1D] border-[#E05A1D] text-white"
                  : "bg-[#0F0F0F] border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#E05A1D]"
              }`}
              data-testid={`timer-preset-${p.seconds}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom */}
        <div className="flex gap-2">
          <input
            type="number"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            placeholder="Custom (sec)"
            className="flex-1 bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-xs focus:border-[#E05A1D] outline-none"
            onKeyDown={(e) => e.key === "Enter" && handleCustomStart()}
            data-testid="timer-custom-input"
          />
          <button
            onClick={handleCustomStart}
            className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white text-xs font-bold uppercase px-3 py-2 transition-colors"
            data-testid="timer-custom-start-btn"
          >
            Go
          </button>
        </div>
      </div>

      <audio ref={audioRef} />
    </div>
  );
}
